import { type FC, type ComponentType } from "react";

import { cn } from "@/shared/lib/utils";
import { Input } from "@/shared/components/ui/input";
import { RadioGroupItem } from "@/shared/components/ui/radio-group";
import { Checkbox } from "@/shared/components/ui/checkbox";

interface OptionCardProps {
  label: string;
  description?: string;
  icon?: ComponentType<{ className?: string }>;
  selected: boolean;
  onClick: () => void;
  variant?: "radio" | "checkbox";
  className?: string;
  value?: string;
  hasTextInput?: boolean;
  textInputPlaceholder?: string;
  textInputValue?: string;
  onTextInputChange?: (value: string) => void;
}

export const OptionCard: FC<OptionCardProps> = ({
  label,
  description,
  icon: Icon,
  selected,
  onClick,
  variant = "radio",
  className,
  value,
  hasTextInput,
  textInputPlaceholder,
  textInputValue,
  onTextInputChange,
}) => {
  if (variant === "radio" && value) {
    return (
      <label
        className={cn(
          "flex w-full cursor-pointer flex-col rounded-lg border-[1.5px] border-solid bg-white p-4 text-left transition-all hover:bg-subtle/50",
          selected ? "border-focus" : "border-primary",
          className,
        )}
        onClick={onClick}
      >
        <div className="flex items-center gap-2">
          <RadioGroupItem value={value} />
          <div className="flex flex-1 flex-col gap-0.5">
            <span className="text-sm text-primary">{label}</span>
            {description && <span className="text-sm text-secondary">{description}</span>}
          </div>
        </div>

        {hasTextInput && selected && (
          <div className="mt-3">
            <Input
              placeholder={textInputPlaceholder}
              value={textInputValue ?? ""}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => onTextInputChange?.(e.target.value)}
            />
          </div>
        )}
      </label>
    );
  }

  const hasIcon = !!Icon;
  const hasDescription = !!description;

  return (
    <button
      type="button"
      className={cn(
        "flex w-full cursor-pointer gap-2 rounded-lg border-[1.5px] border-solid bg-white p-4 text-left transition-all hover:bg-subtle/50",
        hasDescription ? "items-start" : "items-center",
        selected ? "border-focus" : "border-primary",
        className,
      )}
      onClick={onClick}
    >
      {!hasIcon && (
        <Checkbox
          checked={selected}
          className={cn("pointer-events-none", hasDescription && "mt-0.5 shrink-0")}
        />
      )}

      <div className="flex flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-3">
          {Icon && (
            <div className="shrink-0">
              <Icon className="h-5 w-5 text-primary" />
            </div>
          )}
          <span className="text-sm font-medium text-primary">{label}</span>
        </div>
        {description && <span className="text-sm text-secondary">{description}</span>}
      </div>

      {hasIcon && <Checkbox checked={selected} className="pointer-events-none" />}
    </button>
  );
};
